import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Modal,
  message,
  Popconfirm,
  Divider
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const { Option } = Select;

const NoticeList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notices, setNotices] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    keyword: '',
    // status: '',
    // type: '',
  });

  const statusMap = {
    0: { text: '下架', color: 'default' },
    1: { text: '上架', color: 'green' },
  };

  const typeMap = {
    1: { text: '系统公告', color: 'blue' },
    2: { text: '营销公告', color: 'orange' },
  };

  const linkTypeMap = {
    1: { text: '内部页面', color: 'cyan' },
    2: { text: '外部链接', color: 'purple' },
  };

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        keyword: filters.keyword,
        status: filters.status,
        type: filters.type,
      };

      const response = await api.get('/admin/notices/list', { params });
      if (response.code === 200) {
        setNotices(response.data.list || []);
        setPagination({
          ...pagination,
          total: response.data.total || 0,
        });
      } else {
        message.error(response.message || '获取公告列表失败');
      }
    } catch (error) {
      message.error('获取公告列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotices();
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
    });
  };

  const handleSearch = () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchNotices();
  };

  const handleReset = () => {
    setFilters({
      keyword: '',
      status: '',
      type: '',
    });
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchNotices();
  };

  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/admin/notices/${id}`);
      if (response.code === 200) {
        message.success('删除成功');
        fetchNotices();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const handleStatusChange = async (checked, record) => {
    try {
      const response = await api.put(`/admin/notices/${record.id}/status`, {
        status: checked ? 1 : 0,
      });
      
      if (response.code === 200) {
        message.success('状态更新成功');
        fetchNotices();
      } else {
        message.error(response.message || '状态更新失败');
      }
    } catch (error) {
      message.error('状态更新失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '公告类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeInfo = typeMap[type];
        return typeInfo ? <Tag color={typeInfo.color}>{typeInfo.text}</Tag> : '-';
      },
    },
    {
      title: '内容类型',
      dataIndex: 'linkUrl',
      key: 'contentType',
      render: (_, record) => {
        if (record.linkUrl) {
          return <Tag color="blue">链接</Tag>;
        } else {
          return <Tag color="green">富文本</Tag>;
        }
      },
    },
    {
      title: '链接类型',
      dataIndex: 'linkType',
      key: 'linkType',
      render: (linkType) => {
        const linkTypeInfo = linkTypeMap[linkType];
        return linkTypeInfo ? <Tag color={linkTypeInfo.color}>{linkTypeInfo.text}</Tag> : '-';
      },
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusInfo = statusMap[status];
        return statusInfo ? <Tag color={statusInfo.color}>{statusInfo.text}</Tag> : '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text ? new Date(text).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/notices/edit/${record.id}`)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除?"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="标题"
            value={filters.keyword}
            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
            style={{ width: 200 }}
          />
          <Select
            placeholder="公告类型"
            value={filters.type}
            onChange={(value) => setFilters({ ...filters, type: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="1">系统公告</Option>
            <Option value="2">营销公告</Option>
          </Select>
          <Select
            placeholder="状态"
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="0">下架</Option>
            <Option value="1">上架</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
        <div style={{ float: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/notices/create')}
          >
            新建公告
          </Button>
        </div>
      </div>
      
      <Divider />
      
      <Table
        columns={columns}
        dataSource={notices}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        rowKey="id"
        scroll={{ x: 800 }}
      />
    </Card>
  );
};

export default NoticeList;